import * as Sentry from "@sentry/react";
import React from "react";
import Error from "../pages/Error.tsx";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return <Error error={this.state.error} />;
    }

    return this.props.children;
  }
}

const AppErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const handleError = (error: unknown): Error => {
    if (error instanceof Error) {
      // @ts-ignore
      return error;
    }
    // @ts-ignore
    const defaultError = new Error("An unexpected error occurred");
    return defaultError;
  };

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => {
        const processedError = handleError(error);
        return <Error error={processedError} />;
      }}
    >
      <ErrorBoundary>{children}</ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
};

export default AppErrorBoundary;
